import i18n from "../i18n";
import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import moment from 'moment';
import config from '../config';
import { useBpFlags } from '../Responsive';

const tr = (...args) => i18n.t(...args);

const Schedule = () => {

    const { isTablet } = useBpFlags();
    const viewportRef = useRef(null);
    const contentRef  = useRef(null);
    const [scale, setScale] = useState(1);
    const [user, setUser] = useState(null);
    const [currentWeek, setCurrentWeek] = useState(moment().startOf('isoWeek'));
    //const [nextWeek, setNextWeek] = useState(moment().startOf('isoWeek').add(1, 'weeks'));
    const [schedules, setSchedules] = useState([]);
    const [usersSchedules, setUsersSchedules] = useState([]);
    const [categories, setCategories] = useState([]);
    const [areas, setAreas] = useState([]);
    const [users, setUsers] = useState([]); // ? Store all users

    useEffect(() => {
        if (localStorage.getItem("user") !== null) {
            setUser(JSON.parse(localStorage.getItem("user"))); // ? Retrieve user from localStorage
        }
    }, []);

    useEffect(() => {
        fetchAreas();
        fetchUsers(); 
    }, []);

    useEffect(() => {
        fetchCategories();
    }, [areas]);

    useEffect(() => {
        fetchSchedules();
        fetchUsersSchedules();
    }, [categories, user]);

    // Auto-fit scale on tablet: shrink content to fit viewport width, but never grow it
    useEffect(() => {
      if (!isTablet) { setScale(1); return; }
      const compute = () => {
        const vp = viewportRef.current;
        const el = contentRef.current;
        if (!vp || !el) return;
        const vw = vp.clientWidth - 16;           // a little padding
        const cw = el.scrollWidth;                 // content natural width
        const s = Math.max(0.75, Math.min(1, vw / Math.max(1, cw))); // clamp 0.75..1
        setScale(s);
      };
      compute();
      window.addEventListener('resize', compute);
      return () => window.removeEventListener('resize', compute);
    }, [isTablet, schedules, usersSchedules, categories]);

    const fetchSchedules = async () => {
        try {
            const response = await axios.get(tr('{{API_BASE_URL}}/schedules', {
                API_BASE_URL: config.API_BASE_URL
            }));
            setSchedules(response.data);
        } catch (error) {
            console.error('Error fetching schedules:', error);
        }
    };

    const fetchUsersSchedules = async () => {
        try {
            const currentWeekResponse = await axios.get(tr('{{API_BASE_URL}}/users-schedules?week={{var2}}&year={{var3}}', {
                API_BASE_URL: config.API_BASE_URL,
                var2: currentWeek.isoWeek(),
                var3: currentWeek.year()
            }));
            //const nextWeekResponse = await axios.get(`${config.API_BASE_URL}/users-schedules?week=${nextWeek.isoWeek()}&year=${nextWeek.year()}`);
            
            setUsersSchedules([...currentWeekResponse.data]);//, ...nextWeekResponse.data]);
        } catch (error) {
            console.error("Error fetching users' schedules:", error);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get(tr('{{API_BASE_URL}}/users/roles', {
                API_BASE_URL: config.API_BASE_URL
            }));
            const roles = response.data.map(role => ({
                id: role.id,
                name: role.name,
                role: role.role,
                area: null
            }));

            setCategories([...roles, ...areas.map(area => ({
                id: area.id,
                name: area.name,
                role: 'at_service',
                area: area.id
            }))]);
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const fetchAreas = async () => {
        try {
            const response = await axios.get(tr('{{API_BASE_URL}}/areas', {
                API_BASE_URL: config.API_BASE_URL
            }));
            setAreas(response.data);
        } catch (error) {
            console.error('Error fetching areas:', error);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await axios.get(tr('{{API_BASE_URL}}/users', {
                API_BASE_URL: config.API_BASE_URL
            }));
            setUsers(response.data); // ? Store users so we can display their names
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    };

    const getUsersForSlot = (slotId) => {
      return usersSchedules
        .filter(userSchedule => userSchedule.schedule === slotId)
        .map(userSchedule => users.find(u => u.id === userSchedule.user))
        .filter(Boolean); // <-- IMPORTANT: removes undefined users
    };

    const isUserAssigned = (slotId) => {
        if (!user) return false; // ? Prevent error if user is not yet set
        return usersSchedules.some(userSchedule => userSchedule.schedule === slotId && userSchedule.user === user.id);
    };

    const renderTableHeaders = () => {
        return (
            <tr>
                <th>{tr('Role / Area')}</th>
                {[...Array(7)].map((_, i) => (
                    <th key={i} style={{color: currentWeek.clone().day(i + 1).format('D/M/Y') === moment().format('D/M/Y')? 'darkOrange': 'darkBlue'}}>{currentWeek.clone().day(i + 1).format('dddd, D/M')}</th>
                ))}
            </tr>
        );
    };

    const renderTableRows = () => {
        return categories.map((category, rowIndex) => (
            <tr key={rowIndex}>
                <td><strong>{category.name}</strong></td>
                {[...Array(7)].map((_, day) => {

                   const cellSlots = schedules.filter(slot =>
                     slot.day === day &&
                     (
                       (category.area ? // area row: match by area id only
                       slot.area === category.area : slot.area == null && slot.role === category.id) // role row: no area, match role id
                     )
                   );
                    
                    return (
                        <td key={day} style={{ verticalAlign: 'top', minWidth: '145px' }}>
                            <div style={{ display: 'inline-grid', width: '100%', textAlign: 'center' }}>
                                {cellSlots.map((slot, index) => {
                                    const usersInSlot = getUsersForSlot(slot.id);
                                    const isCurrentUserAssigned = isUserAssigned(slot.id);
                                    return usersInSlot?.length>0 && (
                                        <div key={index} style={{ display: 'inline-grid', background: isCurrentUserAssigned ? '#ffebcc' : '#eef' }}>
                                        <b style={{color: currentWeek.clone().day(day + 1).format('D/M/Y') === moment().format('D/M/Y')? 'darkOrange': 'darkBlue', background: currentWeek.clone().day(day + 1).format('D/M/Y') === moment().format('D/M/Y')? '#fee' : '#eef', borderTop: '2px solid '+(currentWeek.clone().day(day + 1).format('D/M/Y') === moment().format('D/M/Y')? 'darkOrange': 'darkBlue'), marginTop: '2px', textShadow: '1px 1px 1px '+(currentWeek.clone().day(day + 1).format('D/M/Y') === moment().format('D/M/Y')? 'black': 'white')}}>{slot.starts.slice(0, -3)} - {slot.ends.slice(0, -3)}</b>
                                            <div style={{ textAlign: 'left', background: 'white' }}>
                                              {(usersInSlot || []).filter(Boolean).map((userInSlot, order) => (
                                                <span key={userInSlot?.id ?? `${slot.id}-${order}`} style={{ fontWeight: userInSlot.id === user?.id ? 'bold' : 'lighter', display: 'block', paddingLeft: usersInSlot?.length>1?'3px':'23px' }}>
                                                        <b style={{color: 'darkBlue'}}>{usersInSlot?.length>1 && (order+1)+')'}</b> <span style={{color: userInSlot.id === user?.id ?'red':'black'}}>{userInSlot.name || '('+userInSlot.username+')'}</span>
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </td>
                    );
                })}
            </tr>
        ));
    };

    if (!user) {
        return <p>{tr('Loading schedule...')}</p>;
    }

    return (
        <div className={isTablet ? 'tablet-viewport' : ''} ref={viewportRef} style={{ marginLeft: isTablet ? 0 : '30px' }}>
            <h2 style={{ marginLeft: isTablet ? '20px' : 0 }}>{tr('Weekly Schedule')}</h2>
            <div className={isTablet ? 'fit-scale-wrap' : ''} style={isTablet ? { '--scale': scale } : undefined}>
              <div className={isTablet ? 'fit-scale' : ''} ref={contentRef} style={{ margin: isTablet ? '20px' : '30px' }}>
                <table border="1">
                    <thead>
                        {renderTableHeaders()}
                    </thead>
                    <tbody>
                        {renderTableRows()}
                    </tbody>
                </table>
              </div>
            </div>
        </div>
    );
};

export default Schedule;
